import { useEffect, useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { AppShell } from "../components/layout/AppShell";
import { Card } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Fields";
import { ConfirmDialog } from "../components/ui/Modal";
import { DataTable, type Column } from "../components/ui/DataTable";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";
import type { EligibleHost } from "../api/types";

/**
 * Round 30 — "Withdrawal Eligibility": a super_admin tool over
 * routes/admin/withdrawal-eligibility.js. This is the "host-eligibility
 * gate" the Diamond Withdraw screen enforces — existence in
 * withdrawal_eligible_hosts (migration 0065) IS eligibility, granted or
 * revoked only here, with the same search-by-public-User-ID flow
 * CoinAdjustmentsPage already uses. The actual enforcement is re-checked
 * server-side on every withdrawal attempt (request_diamond_withdrawal),
 * never just at grant time — this page only manages the allowlist.
 */
interface FoundUser {
  id: string; // internal UUID — only used to target grant/revoke calls
  user_id: number; // public ID
  username: string;
  avatar_url: string | null;
  is_eligible: boolean;
}

export function WithdrawalEligibilityPage() {
  const [searchId, setSearchId] = useState("");
  const [found, setFound] = useState<FoundUser | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isGranting, setIsGranting] = useState(false);

  const [hosts, setHosts] = useState<EligibleHost[]>([]);
  const [isLoadingHosts, setIsLoadingHosts] = useState(true);
  const [revokeTarget, setRevokeTarget] = useState<EligibleHost | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const { showToast } = useToast();

  async function loadHosts() {
    setIsLoadingHosts(true);
    try {
      const rows = await apiRequest<EligibleHost[]>("/admin/withdrawal-eligibility");
      setHosts(rows);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not load eligible hosts", "error");
    } finally {
      setIsLoadingHosts(false);
    }
  }

  useEffect(() => {
    loadHosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSearch() {
    if (!searchId.trim()) return;
    setIsSearching(true);
    setSearchError(null);
    setFound(null);
    try {
      const user = await apiRequest<FoundUser>("/admin/withdrawal-eligibility/lookup", { params: { query: searchId.trim() } });
      setFound(user);
    } catch (err) {
      setSearchError(err instanceof ApiError ? err.message : "Lookup failed");
    } finally {
      setIsSearching(false);
    }
  }

  async function handleGrant() {
    if (!found) return;
    setIsGranting(true);
    try {
      await apiRequest("/admin/withdrawal-eligibility", { method: "POST", body: { id: found.id } });
      showToast(`${found.username} can now withdraw diamonds`, "success");
      setFound((f) => (f ? { ...f, is_eligible: true } : f));
      loadHosts();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not grant eligibility", "error");
    } finally {
      setIsGranting(false);
    }
  }

  async function handleRevoke() {
    if (!revokeTarget) return;
    setIsRevoking(true);
    try {
      await apiRequest(`/admin/withdrawal-eligibility/${revokeTarget.user_id}`, { method: "DELETE" });
      showToast(`${revokeTarget.profiles?.username ?? "User"} can no longer withdraw diamonds`, "success");
      if (found && found.id === revokeTarget.user_id) setFound({ ...found, is_eligible: false });
      loadHosts();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not revoke eligibility", "error");
    } finally {
      setIsRevoking(false);
      setRevokeTarget(null);
    }
  }

  const columns: Column<EligibleHost>[] = [
    { header: "Username", render: (row) => row.profiles?.username ?? "—" },
    { header: "User ID", render: (row) => <span className="font-mono text-xs text-ink-muted">{row.profiles?.user_id ?? "—"}</span> },
    { header: "Granted", render: (row) => new Date(row.granted_at).toLocaleString() },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <Button size="sm" variant="danger" onClick={() => setRevokeTarget(row)}>
          Revoke
        </Button>
      ),
    },
  ];

  return (
    <AppShell title="Withdrawal Eligibility">
      <p className="mb-4 max-w-xl text-xs text-ink-faint">
        Only users granted here can submit a Diamond withdrawal — the Diamond screen's own Wealth-level display never
        decides this. Re-checked server-side on every withdrawal attempt (request_diamond_withdrawal), never just at
        grant time, so revoking here immediately blocks any future request even if the app screen was already open.
      </p>

      <Card className="max-w-xl p-5">
        <div className="flex items-end gap-2">
          <Input
            label="Find user by public User ID"
            placeholder="e.g. 74848506"
            value={searchId}
            onChange={(e) => {
              setSearchId(e.target.value);
              setFound(null);
              setSearchError(null);
            }}
          />
          <Button variant="secondary" onClick={handleSearch} isLoading={isSearching} disabled={!searchId.trim()}>
            Find
          </Button>
        </div>
        {searchError && <p className="mt-1.5 text-xs text-danger">{searchError}</p>}

        {found && (
          <div className="mt-4 flex items-center gap-3 border-t border-surface-border pt-4">
            <Avatar src={found.avatar_url} className="h-10 w-10 rounded-full object-cover" />
            <div>
              <p className="font-semibold text-ink">{found.username}</p>
              <p className="font-mono text-xs text-ink-faint">ID: {found.user_id}</p>
            </div>
            <div className="ml-auto">
              {found.is_eligible ? (
                <Button variant="danger" onClick={() => setRevokeTarget({ user_id: found.id, granted_at: "", profiles: { user_id: found.user_id, username: found.username, avatar_url: found.avatar_url } })}>
                  Revoke Eligibility
                </Button>
              ) : (
                <Button variant="primary" onClick={handleGrant} isLoading={isGranting}>
                  Grant Eligibility
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>

      <div className="mt-8">
        <h2 className="mb-3 text-sm font-semibold text-ink">Currently eligible hosts</h2>
        <DataTable
          columns={columns}
          rows={hosts}
          keyField={(r) => r.user_id}
          isLoading={isLoadingHosts}
          error={null}
          onRetry={loadHosts}
          emptyTitle="No eligible hosts yet"
        />
      </div>

      <ConfirmDialog
        isOpen={!!revokeTarget}
        onClose={() => !isRevoking && setRevokeTarget(null)}
        onConfirm={handleRevoke}
        isLoading={isRevoking}
        isDangerous
        title="Revoke withdrawal eligibility?"
        confirmLabel="Revoke"
        description={
          revokeTarget
            ? `${revokeTarget.profiles?.username ?? "This user"} will no longer be able to submit Diamond withdrawals. Their diamond balance is unaffected.`
            : ""
        }
      />
    </AppShell>
  );
}
